/**
 * VODIČ KROZ PSIHOTERAPIJU CONFIGURATION
 *
 * Content curation config for the /vodic-kroz-psihoterapiju learning hub. This is a
 * curated index of existing blog posts, help articles, and site pages
 * grouped into a numbered curriculum (1.0-5.0). It does not own any content:
 * every item points at a canonical URL elsewhere on the site (a blog post,
 * a help center article, or a static page) and the real title/description
 * for blog and help items is resolved from their frontmatter at render time.
 *
 * Card resolution is deliberately crash-proof: a missing blog or help slug
 * is skipped unless the item explicitly opts into a fallback title and
 * description (`allowFallback: true`), in which case the card still renders
 * and links to the canonical URL even before the underlying content exists.
 *
 * Every item also carries curated `cardTitle`/`cardDescription` copy, written
 * specifically for this guide (question-led, sentence case, "ti" form)
 * instead of reusing the linked content's own SEO title/meta-description.
 * The curated copy always wins over the resolved frontmatter; only the
 * image and href keep coming from the resolved content.
 */

export type TherapyGuideItemType = "blog" | "help" | "page";

export interface TherapyGuideItem {
  type: TherapyGuideItemType;
  /** slugAsParams for "blog"/"help" items, or the full href for "page" items */
  slug: string;
  /** Used only for type "page", or as a fallback href builder for blog/help */
  href?: string;
  /** Required for "page" items, optional fallback for "blog"/"help" items */
  fallbackTitle?: string;
  fallbackDescription?: string;
  /** When true, render a fallback card instead of skipping a missing blog/help slug */
  allowFallback?: boolean;
  /**
   * Curated card title, written for this guide rather than reused from the
   * linked content's own SEO title. When set, it wins over the resolved
   * frontmatter (or page fallback) title. The image and href always come
   * from the resolved content, never from this override.
   */
  cardTitle?: string;
  /** Curated card description, same override rules as `cardTitle`. */
  cardDescription?: string;
}

export interface TherapyGuideSection {
  id: string;
  number: string;
  title: string;
  intro: string;
  items: TherapyGuideItem[];
}

export interface TherapyGuideCard {
  type: TherapyGuideItemType;
  title: string;
  description: string;
  href: string;
  image?: string;
  imageAlt?: string;
  blogPost?: {
    slug: string;
    title: string;
    description: string;
    image: string;
    date: Date;
  };
}

/**
 * Section surface system (ratified palette, 2026-08-06; flattened 2026-08-06).
 * One hue, two semantic surface steps: sections and article cards use the
 * very-light canvas, while the chapter index and article hover use dark cream
 * to communicate grouping and interaction. Sections remain differentiated by
 * number and title, never by decorative category colors.
 */
export interface TherapyGuideAccent {
  /** Number badge pill: soft tint background + readable text */
  badge: string;
  /** Leading category card background + text */
  category: string;
  /** Article card hover surface, kept within the section's color family */
  cardHover: string;
  /** Solid (unblurred, non-transparent) fill for the hero's decorative shapes */
  shapeFill: string;
}

const FLAT_ACCENT: TherapyGuideAccent = {
  badge: "bg-brand/10 text-brand",
  category: "bg-secondary text-foreground",
  cardHover: "group-hover:bg-secondary group-focus-visible:bg-secondary",
  shapeFill: "bg-editorial-quiet",
};

export const THERAPY_GUIDE_ACCENTS: Record<string, TherapyGuideAccent> = {
  "osnove-psihoterapije": FLAT_ACCENT,
  "kome-se-obracas": FLAT_ACCENT,
  "cena-i-trajanje": FLAT_ACCENT,
  "sa-cime-terapija-pomaze": FLAT_ACCENT,
  "terapijski-pravci": FLAT_ACCENT,
};

const DEFAULT_THERAPY_GUIDE_ACCENT: TherapyGuideAccent =
  THERAPY_GUIDE_ACCENTS["osnove-psihoterapije"]!;

export function getTherapyGuideAccent(sectionId: string): TherapyGuideAccent {
  return THERAPY_GUIDE_ACCENTS[sectionId] ?? DEFAULT_THERAPY_GUIDE_ACCENT;
}

export const THERAPY_GUIDE_SECTIONS: TherapyGuideSection[] = [
  {
    id: "osnove-psihoterapije",
    number: "1.0",
    title: "Osnove psihoterapije",
    intro:
      "Šta je psihoterapija, kako izgleda proces i šta možeš da očekuješ, od prve sesije do onoga što radiš između seansi.",
    items: [
      {
        type: "blog",
        slug: "sta-je-psihoterapija-i-kako-izgleda-prvi-razgovor",
        cardTitle: "Kako funkcioniše psihoterapija?",
        cardDescription:
          "Šta terapija jeste, šta nije i kako izgleda prvi razgovor.",
      },
      {
        type: "blog",
        slug: "kako-da-znas-da-ti-je-potrebna-psihoterapija",
        cardTitle: "Da li ti je potrebna terapija?",
        cardDescription:
          "Znaci i situacije koje pokazuju da je vreme da potražiš pomoć.",
      },
      {
        type: "blog",
        slug: "online-psihoterapija-vodic",
        cardTitle: "Kako izgleda online terapija?",
        cardDescription:
          "Efikasnost, cena i kako da zakažeš prvu online sesiju.",
      },
      {
        type: "blog",
        slug: "online-psihoterapija-privatnost-priprema-sesije",
        cardTitle: "Kako da pripremiš prostor za sesiju?",
        cardDescription:
          "Fizička i digitalna privatnost pre razgovora sa terapeutom.",
      },
      {
        type: "blog",
        slug: "sta-raditi-izmedju-psihoterapijskih-seansi",
        cardTitle: "Šta raditi između seansi?",
        cardDescription:
          "Koliko truda uložiti van terapije i kada je dovoljno samo da dođeš.",
      },
      {
        type: "help",
        slug: "vodic-kroz-prvu-sesiju",
        href: "/podrska/klijenti/vodic-kroz-prvu-sesiju",
        cardTitle: "Vodič kroz prvu sesiju",
        cardDescription:
          "Šta se dešava od zakazivanja do kraja prvog razgovora sa terapeutom.",
      },
    ],
  },
  {
    id: "kome-se-obracas",
    number: "2.0",
    title: "Kome se obraćaš",
    intro:
      "Psiholog, psihijatar ili psihoterapeut, ko šta radi, i kako da prepoznaš terapeuta kome možeš da veruješ.",
    items: [
      {
        type: "blog",
        slug: "razlika-izmedju-psihologa-psihijatra-i-psihoterapeuta",
        cardTitle: "Psiholog, psihijatar ili psihoterapeut?",
        cardDescription: "Ko šta radi i kome prvo da se javiš.",
      },
      {
        type: "blog",
        slug: "ko-moze-da-bude-psihoterapeut-u-srbiji",
        cardTitle: "Ko sme da se zove psihoterapeut?",
        cardDescription:
          "Zvanje nije zakonski zaštićeno u Srbiji, evo šta da proveriš.",
      },
      {
        type: "blog",
        slug: "kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
        cardTitle: "Kako da izabereš online psihologa?",
        cardDescription:
          "Pitanja koja da postaviš i znaci da terapeut nije pravi izbor.",
      },
      {
        type: "blog",
        slug: "koje-vestine-treba-da-poseduje-dobar-psihoterapeut",
        cardTitle: "Šta čini dobrog terapeuta?",
        cardDescription: "Veštine i osobine na koje vredi obratiti pažnju.",
      },
      {
        type: "blog",
        slug: "kako-da-budete-sigurni-da-mozete-verovati-svom-psihoterapeutu",
        cardTitle: "Kako da znaš da mu možeš verovati?",
        cardDescription:
          "Znaci poverenja na koje da obratiš pažnju od prvog razgovora.",
      },
      {
        type: "help",
        slug: "izbor-terapeuta",
        href: "/podrska/klijenti/izbor-terapeuta",
        cardTitle: "Kako da izabereš terapeuta na platformi",
        cardDescription:
          "Kako funkcionišu preporuke i šta da uradiš ako ti prvi izbor ne odgovara.",
      },
    ],
  },
  {
    id: "cena-i-trajanje",
    number: "3.0",
    title: "Cena i trajanje",
    intro:
      "Koliko terapija traje, koliko košta i kako izgleda zakazivanje termina na platformi.",
    items: [
      {
        type: "blog",
        slug: "koliko-traje-i-koliko-kosta-psihoterapija",
        cardTitle: "Koliko košta terapija?",
        cardDescription: "Realne cene u Srbiji i online, i od čega zavise.",
      },
      {
        type: "blog",
        slug: "besplatna-i-pristupacna-psihoterapija",
        cardTitle: "Šta ako nemaš novca za terapiju?",
        cardDescription:
          "Besplatne i pristupačnije opcije koje stvarno postoje.",
      },
      {
        type: "help",
        slug: "cene-i-placanje",
        href: "/podrska/klijenti/cene-i-placanje",
        cardTitle: "Cene i plaćanje na platformi",
        cardDescription: "Kako rade paketi, krediti i poklon kartice.",
      },
      {
        type: "help",
        slug: "zakazivanje-i-termini",
        href: "/podrska/klijenti/zakazivanje-i-termini",
        cardTitle: "Kako da zakažeš termin",
        cardDescription:
          "Zakazivanje, otkazivanje i šta se dešava kad terapeut otkaže.",
      },
    ],
  },
  {
    id: "sa-cime-terapija-pomaze",
    number: "4.0",
    title: "Sa čime terapija pomaže",
    intro:
      "Anksioznost, depresija, burnout, veze i gubitak: najčešće teme sa kojima ljudi dolaze na terapiju i kako se sa njima radi.",
    items: [
      {
        type: "blog",
        slug: "anksioznost-simptomi-lecenje",
        cardTitle: "Kako da prepoznaš anksioznost?",
        cardDescription: "Simptomi, kada da se javiš lekaru i šta pomaže.",
      },
      {
        type: "blog",
        slug: "napad-panike-sta-je-to-kako-prepoznati-i-sta-raditi",
        cardTitle: "Šta je napad panike?",
        cardDescription: "Kako da ga prepoznaš i šta da radiš u tom trenutku.",
      },
      {
        type: "blog",
        slug: "depresija-nije-znak-slabosti-razbijamo-stigmu-i-trazimo-pomoc",
        cardTitle: "Zašto depresija nije slabost?",
        cardDescription:
          "Šta je stigma i koji su konkretni koraci ka oporavku.",
      },
      {
        type: "blog",
        slug: "burnout-kako-prepoznati-i-prevladati-iscrpljenost",
        cardTitle: "Šta je burnout?",
        cardDescription: "Faze sagorevanja i kako da se oporaviš.",
      },
      {
        type: "blog",
        slug: "okp-opsesivno-kompulsivni-poremecaj-sta-je-to-i-kako-se-leci",
        cardTitle: "Šta je OCD?",
        cardDescription: "Opsesije, kompulzije i kako se leči.",
      },
      {
        type: "blog",
        slug: "ptsp-razumevanje-traume-i-put-do-oporavka",
        cardTitle: "Šta je PTSP?",
        cardDescription: "Simptomi traume i put do oporavka.",
      },
      {
        type: "blog",
        slug: "par-terapija-kada-i-kako-moze-pomoci-tvom-odnosu",
        cardTitle: "Kada pomaže terapija za parove?",
        cardDescription: "Kako izgleda rad na komunikaciji i poverenju u vezi.",
      },
      {
        type: "blog",
        slug: "kako-se-nositi-s-gubitkom-i-tugom",
        cardTitle: "Kako se nositi sa gubitkom?",
        cardDescription: "Tuga nema raspored, evo kako da tražiš podršku.",
      },
      {
        type: "page",
        slug: "terapija-oblasti",
        href: "/terapija",
        fallbackTitle: "Pogledaj sve oblasti",
        fallbackDescription:
          "Kompletan pregled tema i stanja za koje na platformi možeš da nađeš terapeuta.",
        cardTitle: "Pogledaj sve teme i stanja",
        cardDescription:
          "Kompletan pregled tema za koje na platformi možeš da nađeš terapeuta.",
      },
    ],
  },
  {
    id: "terapijski-pravci",
    number: "5.0",
    title: "Terapijski pravci",
    intro:
      "KBT, geštalt, EMDR, psihodinamska, transakciona analiza, REBT, šema i DBT: pregled najčešćih terapijskih pravaca i kako da izabereš pristup koji ti odgovara.",
    items: [
      {
        type: "blog",
        slug: "vrste-psihoterapije",
        cardTitle: "Koji pravci terapije postoje?",
        cardDescription:
          "Pregled najčešćih pristupa i kako da izabereš onaj koji ti odgovara.",
      },
      {
        type: "blog",
        slug: "kognitivno-bihevioralna-terapija-kako-promeniti-misli-i-zivot",
        cardTitle: "KBT: promena kroz misli i navike",
        cardDescription:
          "Kako kognitivno-bihevioralna terapija menja obrasce razmišljanja i ponašanja.",
      },
      {
        type: "blog",
        slug: "gestalt-terapija-kako-da-se-ponovo-poveses-sa-sobom",
        cardTitle: "Geštalt: kontakt sa onim što osećaš sada",
        cardDescription: "Rad sa telom i emocijama u sadašnjem trenutku.",
      },
      {
        type: "blog",
        slug: "emdr-terapija-lecenje-trauma-i-fobija",
        cardTitle: "EMDR: obrada traume kroz pokrete očiju",
        cardDescription:
          "Kako izgleda sesija i za šta se ovaj pristup najčešće koristi.",
      },
      {
        type: "blog",
        slug: "psihodinamska-psihoterapija",
        cardTitle: "Psihodinamska terapija: obrasci iz prošlosti",
        cardDescription:
          "Kako nesvesni obrasci iz ranijih odnosa utiču na sadašnjost.",
      },
      {
        type: "blog",
        slug: "transakciona-analiza",
        cardTitle: "Transakciona analiza: uloge u odnosima",
        cardDescription:
          "Ego stanja, igre i životni skript koji se ponavljaju.",
      },
      {
        type: "blog",
        slug: "rebt-terapija",
        cardTitle: "REBT: osporavanje iracionalnih uverenja",
        cardDescription:
          "ABC model i kako uverenja o događajima proizvode emocije.",
      },
      {
        type: "blog",
        slug: "shema-terapija",
        cardTitle: "Šema terapija: rani obrasci koji te drže",
        cardDescription:
          "Rane šeme i modusi, i kome ovaj pristup najviše pomaže.",
      },
      {
        type: "blog",
        slug: "dbt-terapija",
        cardTitle: "DBT: veštine za snažne emocije",
        cardDescription:
          "Svesnost, podnošenje distresa, regulacija emocija i odnosi sa drugima.",
      },
      {
        type: "blog",
        slug: "kbt-za-depresiju-koliko-je-danas-jaka-evidencija",
        cardTitle: "Koliko je KBT efikasan za depresiju?",
        cardDescription:
          "Šta pokazuje nova naučna evidencija o efektima na depresiju.",
      },
    ],
  },
];

export type ResolvableBlogPost = {
  slugAsParams: string;
  slug: string;
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  date: Date;
};

export type ResolvableHelpArticle = {
  slugAsParams: string;
  slug: string;
  title: string;
  description: string;
};

export interface TherapyGuideResolutionContext {
  posts: ResolvableBlogPost[];
  helpArticles: ResolvableHelpArticle[];
}

export function resolveTherapyGuideItem(
  item: TherapyGuideItem,
  context: TherapyGuideResolutionContext,
): TherapyGuideCard | null {
  if (item.type === "page") {
    if (!item.href || !item.fallbackTitle || !item.fallbackDescription) {
      return null;
    }

    return {
      type: "page",
      title: item.cardTitle ?? item.fallbackTitle,
      description: item.cardDescription ?? item.fallbackDescription,
      href: item.href,
    };
  }

  if (item.type === "blog") {
    const post = context.posts.find(
      (candidate) => candidate.slugAsParams === item.slug,
    );

    if (post) {
      return {
        type: "blog",
        title: item.cardTitle ?? post.title,
        description: item.cardDescription ?? post.description,
        href: post.slug,
        image: post.image,
        imageAlt: post.imageAlt || post.title,
        blogPost: {
          slug: post.slug,
          title: item.cardTitle ?? post.title,
          description: item.cardDescription ?? post.description,
          image: post.image,
          date: post.date,
        },
      };
    }

    if (item.allowFallback && item.fallbackTitle && item.fallbackDescription) {
      return {
        type: "blog",
        title: item.cardTitle ?? item.fallbackTitle,
        description: item.cardDescription ?? item.fallbackDescription,
        href: item.href ?? `/blog/${item.slug}`,
      };
    }

    return null;
  }

  const article = context.helpArticles.find(
    (candidate) => candidate.slugAsParams === item.slug,
  );

  if (article) {
    return {
      type: "help",
      title: item.cardTitle ?? article.title,
      description: item.cardDescription ?? article.description,
      href: article.slug,
    };
  }

  if (
    item.allowFallback &&
    item.fallbackTitle &&
    item.fallbackDescription &&
    item.href
  ) {
    return {
      type: "help",
      title: item.cardTitle ?? item.fallbackTitle,
      description: item.cardDescription ?? item.fallbackDescription,
      href: item.href,
    };
  }

  return null;
}

export interface TherapyGuideResolvedSection {
  id: string;
  number: string;
  title: string;
  intro: string;
  accent: TherapyGuideAccent;
  cards: TherapyGuideCard[];
}

export function resolveTherapyGuideSection(
  section: TherapyGuideSection,
  context: TherapyGuideResolutionContext,
): TherapyGuideResolvedSection {
  return {
    id: section.id,
    number: section.number,
    title: section.title,
    intro: section.intro,
    accent: getTherapyGuideAccent(section.id),
    cards: section.items
      .map((item) => resolveTherapyGuideItem(item, context))
      .filter((card): card is TherapyGuideCard => card !== null),
  };
}

export function resolveTherapyGuideSections(
  context: TherapyGuideResolutionContext,
): TherapyGuideResolvedSection[] {
  return THERAPY_GUIDE_SECTIONS.map((section) =>
    resolveTherapyGuideSection(section, context),
  );
}
